import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { ARTICLE_MAIN_CONTENT_NODE_ID } from '../../constants';
import withRouter from 'react-router/lib/withRouter';
import MediaQuery from '../common/media_query';
import SeeAllSlides from './slideshow/see_all_slides';
import DesktopLayout from './slideshow/desktop_layout';
import NonDesktopLayout from './slideshow/non_desktop_layout';
import {
  getPreviousSlideLocation,
  getNextSlideLocation,
  getSlideIndexInHash,
  scrollToTopDesktopAd,
  refreshAds
} from '../../utils/slideshow';
import { sendAnalyticsEvent, sendDataLayerEvent } from '../../utils/analytics';
import { currentBreakpoint } from '../../utils/dom';
import { isJWVideoPlaying, pauseJWPlayer } from '../../utils/video/jw';
import { redirectToPath } from '../../utils/url';
import {each, find, once, throttle} from 'lodash';

const INTERNAL_VIDEO_SECTION_TYPE = 'video:internal';

const isLeftArrow = ({ key, keyCode }) =>
  key === 'ArrowLeft' || keyCode === 37;

const isRightArrow = ({ key, keyCode }) =>
  key === 'ArrowRight' || keyCode === 39;

// Hack to convert the intro slide override into a makeshift opener image.
const toOpenerImage = ({
  width, height, src_full, credit, impressiontracker, clicktracker, alt_title
}) => ({
  type: width + 'x' + height,
  alt_title: alt_title || null,
  asset: {
    src: {
      full: src_full
    },
    credit,
    impressiontracker,
    clicktracker
  }
});

export class Slideshow extends Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      seeAllSlides: false,
      slideIndex: 0
    };
    this.handleChangeSlide = throttle(
      this.props.changeSlide.bind(this),
      this.props.throttleWait
    );

    this.updateUpNext = once(this.props.pickBridgeRecommendation);
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
    this.updateSlideIndex(this.context.location);
  }

  componentDidUpdate() {
    this.updateSlideIndex(this.context.location);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  updateSlideIndex = (location) => {
    const slideIndex = getSlideIndexInHash(location);
    if (slideIndex <= this.props.slideshowSections.length && this.state.slideIndex !== slideIndex) {
      this.setState({ slideIndex });
    }
  };

  handleKeyDown = (event) => {
    if (this.state.seeAllSlides || currentBreakpoint() !== 'desktop') {
      return;
    }

    const { slideIndex } = this.state;

    const slideCount = this.props.slideshowSections.length;
    const onLastSlide = slideIndex === slideCount;

    if (isRightArrow(event) && onLastSlide && this.props.upNext.url) {
      redirectToPath(this.props.upNext.url);
      return;
    }

    let getSlideLocation;
    if (isLeftArrow(event)) {
      getSlideLocation = getPreviousSlideLocation;
    } else if (isRightArrow(event)) {
      getSlideLocation = getNextSlideLocation;
    } else {
      return;
    }

    const newSlideLocation =
      getSlideLocation(slideIndex, this.context.location, slideCount);

    if (!newSlideLocation) {
      return;
    } else if (slideIndex === 0) {
      sendAnalyticsEvent('Slideshow', 'Click', 'Begin Keyboard');
      sendDataLayerEvent('Click', {
        actionType: 'Begin Slideshow',
        itemName: 'Keyboard',
        slideshowComponent: 'Keyboard',
        pageModule: 'Slideshow'
      });
      scrollToTopDesktopAd();
    } else {
      sendAnalyticsEvent('Slideshow', 'Slide Arrow', 'Keyboard');
      sendDataLayerEvent('Click', {
        actionType: isRightArrow(event) ? 'Next Slide' : 'Previous Slide',
        itemName: 'Keyboard',
        slideshowComponent: 'Keyboard',
        pageModule: 'Slideshow'
      });
    }

    this.props.router.push(newSlideLocation);
    this.handleChangeSlide();
    refreshAds();
  };

  handleThumbnailClick = () => {
    this.toggleSeeAllSlides();
    this.handleChangeSlide();
    scrollToTopDesktopAd();
  };

  getDesktopOpenerImage() {
    if (this.props.asset_opener_intro_slide_override) {
      return toOpenerImage(this.props.asset_opener_intro_slide_override);
    }

    const type = this.props.hasFullWidthIntroSlide || this.props.isFullWidth
      ? '4x3'
      : '5x6';
    return find(this.props.openerImages, { type });
  }

  pauseAllPlayingJWVideos = () =>
    each(this.sections(), ({ type, ...props }) => {
      if (type !== INTERNAL_VIDEO_SECTION_TYPE || !isJWVideoPlaying(props)) {
        return;
      }
      pauseJWPlayer(props);
    });

  toggleSeeAllSlides = () =>
    this.setState({ seeAllSlides: !this.state.seeAllSlides });

  handleSeeAllSlidesClick = () => {
    sendAnalyticsEvent(
      'Slideshow',
      this.state.seeAllSlides
        ? 'Go Back'
        : 'See All Slides'
    );

    if (!this.state.seeAllSlides) {
      sendDataLayerEvent('Click', {
        actionType: 'See All Slides',
        itemName: 'Slideshow',
        pageModule: 'Slideshow',
        slideshowComponent: 'Navigation'
      });
    }

    if (!this.state.seeAllSlides) {
      this.pauseAllPlayingJWVideos();
    }
    scrollToTopDesktopAd();
    this.toggleSeeAllSlides();
  };

  sections = () => [
    ...this.props.bodySections,
    ...this.props.slideshowSections
  ];

  render() {
    const { hasFullWidthIntroSlide, isFullWidth, openerImages,
      sectionsProps, slideshowSections, socialIconProps, upNext,
      contentContainerId, bodySections
    } = this.props;
    const { slideIndex } = this.state;

    const openerImage5x6 = find(openerImages, { type: '5x6' });
    const desktopOpenerImage = this.getDesktopOpenerImage();
    const slideIndexProp = slideIndex <= slideshowSections.length ? slideIndex : 0;

    const slideshowProps = {
      bodySections,
      openerImages,
      sectionsProps,
      slideIndex: slideIndexProp,
      slideshowSections,
      socialIconProps
    };

    let seeAllSlides;
    if (this.state.seeAllSlides) {
      seeAllSlides = (
        <SeeAllSlides adsState={sectionsProps.adsState}
          handleGoBackClick={this.handleSeeAllSlidesClick}
          handleThumbnailClick={this.handleThumbnailClick}
          openerImage={desktopOpenerImage}
          slideshowSections={slideshowSections}
        />
      );
    }

    return (
      <div className="slideshow-wrapper">
        <MediaQuery breakpoint="notDesktop">
          <NonDesktopLayout {...slideshowProps}
            openerImage={openerImage5x6}
            relatedEntries={this.props.relatedEntries}
          />
        </MediaQuery>
        <MediaQuery breakpoint="desktop">
          {seeAllSlides}
          <DesktopLayout {...slideshowProps}
            handleChangeSlide={this.handleChangeSlide}
            handleSeeAllSlidesClick={this.handleSeeAllSlidesClick}
            hasFullWidthIntroSlide={hasFullWidthIntroSlide}
            isFullWidth={isFullWidth}
            openerImage={desktopOpenerImage}
            shouldDisplay={!this.state.seeAllSlides}
            contentContainerId={contentContainerId}
            socialIconProps={socialIconProps}
            upNext={upNext}
            updateUpNext={this.updateUpNext}
            relatedEntries={this.props.relatedEntries}
          />
        </MediaQuery>
      </div>
    );
  }
}

Slideshow.propTypes = {
  bodySections: PropTypes.arrayOf(PropTypes.object).isRequired,
  changeSlide: PropTypes.func.isRequired,
  hasFullWidthIntroSlide: PropTypes.bool.isRequired,
  isFullWidth: PropTypes.bool.isRequired,
  openerImages: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string.isRequired
  })).isRequired,
  asset_opener_intro_slide_override: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    src_full: PropTypes.string,
    credit: PropTypes.string,
    alt_title: PropTypes.string
  }),
  router: PropTypes.object,
  sectionsProps: PropTypes.shape({
    adsState: PropTypes.object.isRequired
  }).isRequired,
  socialIconProps: PropTypes.object.isRequired,
  slideshowSections: PropTypes.arrayOf(PropTypes.object).isRequired,
  throttleWait: PropTypes.number.isRequired,
  contentContainerId: PropTypes.string.isRequired,
  pickBridgeRecommendation: PropTypes.func.isRequired,
  upNext: PropTypes.object.isRequired,
  relatedEntries: PropTypes.array
};

Slideshow.contextTypes = {
  location: PropTypes.object
};

Slideshow.defaultProps = {
  throttleWait: 500,
  contentContainerId: ARTICLE_MAIN_CONTENT_NODE_ID
};

export default withRouter(Slideshow);
